import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { CheckCircle, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import type { OrganizationDataWithMembership } from '@/lib/types';

interface OrganizationJoinSuccessProps {
  organization: OrganizationDataWithMembership;
  onContinue?: () => void;
}

export function OrganizationJoinSuccess({ organization, onContinue }: OrganizationJoinSuccessProps) {
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(3);

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          handleRedirect();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleRedirect = () => {
    if (onContinue) {
      onContinue();
    } else {
      navigate({ to: '/campaigns' });
    }
  };

  return (
    <motion.div 
      className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="max-w-4xl mx-auto">
        <motion.div 
          className="text-center mb-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome to {organization.name}!</h1>
          <p className="text-lg text-gray-700">You've successfully joined the organization</p>
        </motion.div>
        
        <motion.div
          className="text-center space-y-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <div className="flex justify-center">
            <CheckCircle className="w-16 h-16 text-green-500" />
          </div>

          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-gray-900">You're all set!</h3>
            <p className="text-gray-700 max-w-md mx-auto">
              You can now access campaigns, manage ads, and collaborate with your team members in <strong>{organization.name}</strong>.
            </p>
          </div>

          <div className="pt-6 space-y-4 flex flex-col items-center">
            <Button 
              onClick={handleRedirect}
              size="lg"
              className="w-full max-w-xs bg-[#290BD9] hover:bg-[#2309C5] text-white flex items-center justify-center space-x-2"
            >
              <span>CONTINUE</span>
              <ArrowRight className="h-4 w-4" />
            </Button>
            
            {countdown > 0 && (
              <p className="text-sm text-gray-500">
                Automatically redirecting in {countdown} seconds...
              </p>
            )}
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}